import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import MentionEditorAdd from './MentionEditorAdd';
import MentionEditorEdit from './MentionEditorEdit';
import './mention.css';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import './tasklist.css'
import './addtask.css'
import { formatDateForInput, formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import { hasFeatureAccess } from '../../utils/featureLock';
import DateInput from '../components/Forms/DateInput';
import axiosInstance from '../../services/AxiosInstance';
import { to12HourDisplay } from '../../utils/taskTime';
import { fetchTaskAttachmentBlob } from '../../utils/taskAttachment';
import CommonTable from '../components/CommonTable/CommonTable';
import moment from 'moment';

const formatDate = formatDateForInput;

const idsMatch = (a, b) => a != null && b != null && String(a) === String(b);

// Helper functions for 12-hour time format
const convert24To12Hour = (time24) => {
  if (!time24) return { hour: 12, minute: 0, ampm: 'PM' };
  const [hours, minutes] = time24.split(':').map(Number);
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  return { hour: hour12, minute: minutes || 0, ampm };
};

const convert12To24Hour = (hour, minute, ampm) => {
  let hour24 = parseInt(hour) || 12;
  const min = parseInt(minute) || 0;
  
  if (ampm === 'PM' && hour24 !== 12) {
    hour24 += 12;
  } else if (ampm === 'AM' && hour24 === 12) {
    hour24 = 0;
  }
  
  return `${String(hour24).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

// Parse 12-hour time string to minutes since midnight for comparison
const parse12HourToMinutes = (time12) => {
  if (!time12) return 0;
  const m = String(time12).match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return 0;
  let hour = parseInt(m[1], 10);
  const min = parseInt(m[2], 10) || 0;
  const ampm = m[3].toUpperCase();
  if (ampm === 'PM' && hour !== 12) hour += 12;
  else if (ampm === 'AM' && hour === 12) hour = 0;
  return hour * 60 + min;
};

// Helper function to get day of month and weekday info from start date
const getDateInfo = (startDate) => {
  if (!startDate) return { dayOfMonth: null, weekday: null, weekdayName: null, weekOfMonth: null };
  
  const date = new Date(startDate + 'T00:00:00');
  const dayOfMonth = date.getDate();
  const weekday = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const weekOfMonth = Math.ceil(dayOfMonth / 7);
  
  const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const weekdayName = weekdayNames[weekday];
  
  return { dayOfMonth, weekday, weekdayName, weekOfMonth };
};

// Helper function to get ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
const getOrdinalSuffix = (num) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

const TaskForm = ({
  mode = 'add', // 'add' or 'edit'
  initialData = null,
  onSubmit,
  onCancel,
  availableAssignees = [],
  Tags = [],
  observers = [],
  participants = [],
  projects = [],
  Tasks = [],
  Mentions = [],
  delta = null,
  Description = '',
  userdata = null,
  isRedirecting = false,
  prefillProjectId = null,
  prefillProjectData = null,
  prefillDocumentData = null,
  prefillForumData = null,
  branches = [],
  departments = [],
  subDepartments = []
}) => {
  const [isOpen1, setIsOpen1] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [isOpen3, setIsOpen3] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const fileInputRef = useRef(null);
  const forumPrefillUsersLoadedRef = useRef(false);
  const [hasAssigneeAccess, setHasAssigneeAccess] = useState(false);
  const [hasObserverAccess, setHasObserverAccess] = useState(false);
  const [hasEmailReminderAccess, setHasEmailReminderAccess] = useState(false);
  const [hasRepetitiveTaskAccess, setHasRepetitiveTaskAccess] = useState(false);
  const [hasDocumentUploadAccess, setHasDocumentUploadAccess] = useState(false);
  const [hasBranchAccess, setHasBranchAccess] = useState(false);
  const [hasDepartmentAccess, setHasDepartmentAccess] = useState(false);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [filteredDepartments, setFilteredDepartments] = useState(departments);
  const [filteredAssignees, setFilteredAssignees] = useState(availableAssignees);
  const [filteredObservers, setFilteredObservers] = useState(observers);
  const [filteredParticipants, setFilteredParticipants] = useState(participants);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState(subDepartments);
  const [dayTasks, setDayTasks] = useState([]);
  const [dayTasksLoading, setDayTasksLoading] = useState(false);

  const statusMap = {
    'Pending': 0,
    'In Progress': 1,
    'Postponed': 2,
    'Done': 3,
  };

  const returnStatusMap = {
    0: 'Pending',
    1: 'In Progress',
    2: 'Postponed',
    3: 'Done',
  };

  // Check feature access on component mount
  useEffect(() => {
    const checkFeatureAccess = async () => {
      try {
        setFeatureLoading(true);
        const [assigneeAccess, observerAccess, emailReminderAccess, repetitiveTaskAccess, documentUploadAccess, branchAccess, departmentAccess] = await Promise.all([
          hasFeatureAccess('adding_assignees'),
          hasFeatureAccess('adding_observers'),
          hasFeatureAccess('email_reminders'),
          hasFeatureAccess('repetitive_tasks'),
          hasFeatureAccess('uploading_of_documents'),
          hasFeatureAccess('branch'),
          hasFeatureAccess('department')
        ]);
        setHasAssigneeAccess(assigneeAccess);
        setHasObserverAccess(observerAccess);
        setHasEmailReminderAccess(emailReminderAccess);
        setHasRepetitiveTaskAccess(repetitiveTaskAccess);
        setHasDocumentUploadAccess(documentUploadAccess);
        setHasBranchAccess(branchAccess);
        setHasDepartmentAccess(departmentAccess);
      } catch (error) {
        console.error('Error checking feature access:', error);
        setHasAssigneeAccess(false);
        setHasObserverAccess(false);
        setHasEmailReminderAccess(false);
        setHasRepetitiveTaskAccess(false);
        setHasDocumentUploadAccess(false);
        setHasBranchAccess(false);
        setHasDepartmentAccess(false);
      } finally {
        setFeatureLoading(false);
      }
    };

    checkFeatureAccess();
  }, []);

  // Filter departments and sub-departments based on document, project, or forum prefill data
  useEffect(() => {
    const prefill = prefillDocumentData
      || (mode === 'add' && prefillForumData ? prefillForumData : null)
      || (mode === 'add' && prefillProjectData ? prefillProjectData : null);
    if (mode === 'add' && prefill) {
      const BranchId = prefill.BranchId || prefill.branchIds || [];
      const DepartmentId = prefill.DepartmentId || prefill.departmentIds || [];
      const SubDepartmentId = prefill.SubDepartmentId || prefill.subDepartmentIds || [];
      
      // Filter departments based on selected branches
      if (BranchId && BranchId.length > 0) {
        const filteredDepts = departments.filter(dept =>
          BranchId.some((branchId) => idsMatch(branchId, dept.branch_id))
        );
        setFilteredDepartments(filteredDepts);
        
        // Filter sub-departments based on selected departments
        if (DepartmentId && DepartmentId.length > 0) {
          const filteredSubDepts = subDepartments.filter(subDept =>
            DepartmentId.some((deptId) => idsMatch(deptId, subDept.department_id))
          );
          setFilteredSubDepartments(filteredSubDepts);
        } else {
          setFilteredSubDepartments([]);
        }
      } else {
        // No branches selected, show all departments
        setFilteredDepartments(departments);
        
        // Filter sub-departments based on selected departments
        if (DepartmentId && DepartmentId.length > 0) {
          const filteredSubDepts = subDepartments.filter(subDept =>
            DepartmentId.some((deptId) => idsMatch(deptId, subDept.department_id))
          );
          setFilteredSubDepartments(filteredSubDepts);
        } else {
          setFilteredSubDepartments(subDepartments);
        }
      }
    }
  }, [prefillDocumentData, prefillForumData, prefillProjectData, departments, subDepartments, mode]);

  // Set initial values based on mode
  const getInitialValues = () => {
    // Handle edit mode
    if (mode === 'edit' && initialData) {
      return {
        TaskName: initialData.TaskName || '',
        ParentTaskId: initialData.ParentTaskId || '',
        ProjectId: initialData.ProjectId || '',
        Description: initialData.Description || '',
        Mentions: initialData.Mentions || [],
        Priority: initialData.priority || '',
        Status: initialData.Status || '',
        StartDate: initialData.StartDate || '',
        Deadline: initialData.Deadline || '',
        StartTime: initialData.StartTime ? to12HourDisplay(initialData.StartTime) : '12:00 PM',
        EndTime: initialData.EndTime ? to12HourDisplay(initialData.EndTime) : '01:00 PM',
        BranchId: initialData?.branches ? initialData.branches.map(branch => branch.id) : (initialData?.Branch?.id ? [initialData.Branch.id] : []),
        DepartmentId: initialData?.departments ? initialData.departments.map(department => department.id) : (initialData?.Department?.id ? [initialData.Department.id] : []),
        SubDepartmentId: initialData?.subDepartments ? initialData.subDepartments.map(sd => sd.sub_department_id) : (initialData?.subDepartments?.id ? [initialData.SubDepartment.id] : []),
        RepeatType: initialData.RepeatType || '',
        RepeatInterval: initialData.RepeatInterval || '',
        RepeatUntil: initialData.RepeatUntil || '',
        RepeatNeverEnds: initialData.RepeatNeverEnds || false,
        WeeklyDays: initialData.WeeklyDays || [],
        MonthlyRepeatType: initialData.MonthlyRepeatType || 'day',
        AllowAssigneeUpdate: initialData.AllowAssigneeUpdate || false,
        SendWarning: initialData.SendWarning || false,
        CloseChecklist: initialData.CloseChecklist || false,
        CreatedBy: initialData.CreatedBy || '',
        assigneeList: initialData.assigneeList || [],
        Email: initialData.Email || '',
        ReminderEmail: initialData.ReminderEmail || '',
        TaskStatusEmail: initialData.TaskStatusEmail || '',
        Tags: initialData.Tags || [],
        Files: initialData.Files || [],
        reminderinternalmessage: initialData.reminderinternalmessage || true,
        reminderthrougthemail: initialData.reminderthrougthemail || false,
        MarkHighPriority: initialData.MarkHighPriority || false,
        TaskPin: initialData.TaskPin || false,
        Participants: initialData.Participants || [],
        Observers: initialData.Observers || [],
        Checklist: (Array.isArray(initialData.Checklist) && initialData.Checklist.length > 0)
          ? initialData.Checklist
          : [{ value: '', isEditing: true, checked: false, id: Date.now() + Math.random() }],
        Delta: initialData.delta || null,
        IsRepeatable: initialData?.IsRepeatable ?? false,
        email_reminder: initialData.email_reminder || false,
        is_assignee_update_allowed: initialData.is_assignee_update_allowed || false,
        CreateAsIndividual: initialData.CreateAsIndividual || initialData.create_as_individual || false,
      };
    } else if (mode === 'add' && prefillForumData) {
      return {
        TaskName: prefillForumData.title || '',
        ParentTaskId: '',
        ProjectId: '',
        Description: prefillForumData.description || '',
        Mentions: [],
        AllowShortCut: false,
        Priority: 'Medium',
        Status: '',
        StartDate: formatDate(new Date()),
        Deadline: '',
        StartTime: '12:00 PM',
        EndTime: '01:00 PM',
        BranchId: prefillForumData.BranchId || [],
        DepartmentId: prefillForumData.DepartmentId || [],
        SubDepartmentId: prefillForumData.SubDepartmentId || [],
        RepeatType: '',
        RepeatInterval: '',
        RepeatUntil: formatDate(new Date()),
        RepeatNeverEnds: false,
        WeeklyDays: [],
        MonthlyRepeatType: 'day',
        AllowAssigneeUpdate: false,
        SendWarning: false,
        CloseChecklist: false,
        CreatedBy: '',
        assigneeList: prefillForumData.assignees || [],
        Email: '',
        ReminderEmail: '',
        TaskStatusEmail: '',
        Tags: [],
        Files: [],
        reminderinternalmessage: true,
        reminderthrougthemail: false,
        MarkHighPriority: false,
        TaskPin: false,
        Participants: [],
        Observers: [],
        Checklist: [{ value: '', isEditing: true, checked: false, id: Date.now() + Math.random() }],
        Delta: null,
        IsRepeatable: false,
        email_reminder: false,
        is_assignee_update_allowed: false,
        CreateAsIndividual: false,
      };
    } else if (mode === 'add' && prefillDocumentData) {
      return {
        TaskName: `Upload New Version of Document: ${prefillDocumentData.TaskName}` || '',
        ParentTaskId: '',
        ProjectId: prefillProjectId || '',
        Description: '',
        Mentions: [],
        AllowShortCut: false,
        Priority: '',
        Status: '',
        StartDate: formatDate(new Date()),
        Deadline: prefillDocumentData.expiryDate ? formatDate(prefillDocumentData.expiryDate) : '',
        StartTime: '12:00 PM',
        EndTime: '01:00 PM',
        BranchId: prefillDocumentData.BranchId || [],
        DepartmentId: prefillDocumentData.DepartmentId || [],
        SubDepartmentId: prefillDocumentData.SubDepartmentId || [],
        RepeatType: '',
        RepeatInterval: '',
        RepeatUntil: formatDate(new Date()),
        RepeatNeverEnds: false,
        WeeklyDays: [],
        MonthlyRepeatType: 'day',
        AllowAssigneeUpdate: false,
        SendWarning: false,
        CloseChecklist: false,
        CreatedBy: '',
        assigneeList: prefillDocumentData.assignee ? [prefillDocumentData.assignee] : [],
        Email: '',
        ReminderEmail: '',
        TaskStatusEmail: '',
        Tags: [],
        Files: [],
        reminderinternalmessage: true,
        reminderthrougthemail: false,
        MarkHighPriority: false,
        TaskPin: false,
        Participants: [],
        Observers: [],
        Checklist: [
          { value: '', isEditing: true, checked: false, id: Date.now() + Math.random() },
        ],
        Delta: null,
        IsRepeatable: false,
        email_reminder: false,
        is_assignee_update_allowed: false,
        CreateAsIndividual: false,
      };
    }

    const branchIds = (prefillProjectData && (prefillProjectData.BranchId || prefillProjectData.branchIds)) ? (prefillProjectData.BranchId || prefillProjectData.branchIds) : [];
    const departmentIds = (prefillProjectData && (prefillProjectData.DepartmentId || prefillProjectData.departmentIds)) ? (prefillProjectData.DepartmentId || prefillProjectData.departmentIds) : [];
    const subDepartmentIds = (prefillProjectData && (prefillProjectData.SubDepartmentId || prefillProjectData.subDepartmentIds)) ? (prefillProjectData.SubDepartmentId || prefillProjectData.subDepartmentIds) : [];
    return {
      TaskName: '',
      ParentTaskId: '',
      ProjectId: prefillProjectId || '',
      Description: '',
      Mentions: [],
      AllowShortCut: false,
      Priority: '',
      Status: '',
      StartDate: formatDate(new Date()),
      Deadline: '',
      StartTime: '12:00 PM',
      EndTime: '01:00 PM',
      BranchId: branchIds,
      DepartmentId: departmentIds,
      SubDepartmentId: subDepartmentIds,
      RepeatType: '',
      RepeatInterval: '',
      RepeatUntil: formatDate(new Date()),
      RepeatNeverEnds: false,
      WeeklyDays: [],
      MonthlyRepeatType: 'day',
      AllowAssigneeUpdate: false,
      SendWarning: false,
      CloseChecklist: false,
      CreatedBy: '',
      assigneeList: [],
      Email: '',
      ReminderEmail: '',
      TaskStatusEmail: '',
      Tags: [],
      Files: [],
      reminderinternalmessage: true,
      reminderthrougthemail: false,
      MarkHighPriority: false,
      TaskPin: false,
      Participants: [],
      Observers: [],
      Checklist: [
        { value: '', isEditing: true, checked: false, id: Date.now() + Math.random() },
      ],
      Delta: null,
      IsRepeatable: false,
      email_reminder: false,
      is_assignee_update_allowed: false,
      CreateAsIndividual: false,
    };
  };

  // Memoize initial values to prevent unnecessary resets
  const initialValues = useMemo(() => {
    const values = getInitialValues();
    console.log('Initial values computed:', values);
    console.log('Prefill document data:', prefillDocumentData);
    return values;
  }, [mode, initialData, prefillProjectId, prefillProjectData, prefillDocumentData, prefillForumData?.comment_id]);

  const isOrgPrefilledFromProject = Boolean(
    prefillProjectId && prefillProjectData &&
    ((prefillProjectData.BranchId || prefillProjectData.branchIds || []).length > 0 ||
     (prefillProjectData.DepartmentId || prefillProjectData.departmentIds || []).length > 0 ||
     (prefillProjectData.SubDepartmentId || prefillProjectData.subDepartmentIds || []).length > 0)
  );

  // Fetch users based on branch and department
  const fetchUsersByBranchAndDepartment = async (branchId, departmentId) => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.corporateId) return;

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(userData.corporateId));
      
      if (branchId) {
        params.append('branch', branchId);
      }
      if (departmentId) {
        params.append('department', departmentId);
      }

      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      
      // Filter out the currently logged-in user
      const filteredUsers = users.filter(user => user.id !== userData.id);
      
      return filteredUsers;
    } catch (error) {
      console.error('Error fetching users by branch/department:', error);
      return [];
    }
  };

  // Fetch users based on filter criteria (multiple branches and departments)
  const fetchUsersByFilters = async (branchIds, departmentIds, subDepartmentIds) => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.corporateId) return;

      const params = new URLSearchParams();
      params.append('corporateId', parseInt(userData.corporateId));
      
      // Add branch filters - use comma-separated values
      if (branchIds && branchIds.length > 0) {
        params.append('branch', branchIds.join(','));
      }
      
      // Add department filters - use comma-separated values
      if (departmentIds && departmentIds.length > 0) {
        params.append('department', departmentIds.join(','));
      }

      // Add sub-department filters - use comma-separated values
      if (subDepartmentIds && subDepartmentIds.length > 0) {
        params.append('sub_department', subDepartmentIds.join(','));
      }

      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      
      // Filter out the currently logged-in user
      const filteredUsers = users.filter(user => user.id !== userData.id);
      
      return filteredUsers;
    } catch (error) {
      console.error('Error fetching users by filters:', error);
      return [];
    }
  };

  // Filter departments based on selected branch
  const handleBranchChange = async (selectedBranches, setFieldValue, currentDepartmentIds = [], currentSubDepartmentIds = [], currentAssignees = [], currentObservers = [], currentParticipants = []) => {
    const branchIds = selectedBranches ? selectedBranches.map(option => option.value) : [];
    setFieldValue('BranchId', branchIds);
    
    if (branchIds.length > 0) {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        
        // Call API to fetch departments filtered by selected branches
        // Make API calls for each branch and combine results
        const departmentPromises = branchIds.map(branchId => 
          axiosInstance.get(`/settings/departments?corporate_id=${corporateId}&branch_id=${branchId}`)
        );
        const departmentResponses = await Promise.all(departmentPromises);
        const allDepartments = [];
        departmentResponses.forEach(res => {
          const data = res.data;
          const depts = data.data || data || [];
          // Remove duplicates based on id
          depts.forEach(dept => {
            if (!allDepartments.find(d => d.id === dept.id)) {
              allDepartments.push(dept);
            }
          });
        });
        setFilteredDepartments(allDepartments);
        const relatedDepartmentIds = allDepartments.map(d => d.id);
        
        // Keep only valid departments (that still belong to selected branches)
        const validDepartmentIds = currentDepartmentIds.filter(deptId => relatedDepartmentIds.includes(deptId));
        setFieldValue('DepartmentId', validDepartmentIds);
        
        // Call API to fetch sub-departments filtered by selected branches
        const subDepartmentPromises = branchIds.map(branchId => {
          // Try camel-case endpoint first, then fallback to lowercase
          return axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}&branch_id=${branchId}`)
            .catch(() => axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}&branch_id=${branchId}`));
        });
        const subDepartmentResponses = await Promise.all(subDepartmentPromises);
        const allSubDepartments = [];
        subDepartmentResponses.forEach(res => {
          const data = res.data;
          const subDepts = data.data || data || [];
          // Remove duplicates based on id
          subDepts.forEach(subDept => {
            if (!allSubDepartments.find(sd => sd.id === subDept.id)) {
              allSubDepartments.push(subDept);
            }
          });
        });
        // Further filter by valid departments
        const subsForBranches = allSubDepartments.filter(sd => relatedDepartmentIds.includes(sd.department_id));
        setFilteredSubDepartments(subsForBranches);
        
        // Keep only valid subdepartments (that still belong to valid departments)
        const validSubDepartmentIds = currentSubDepartmentIds.filter(subDeptId => {
          const subDept = subsForBranches.find(sd => sd.id === subDeptId);
          return subDept && validDepartmentIds.includes(subDept.department_id);
        });
        setFieldValue('SubDepartmentId', validSubDepartmentIds);
        
        // Call API to fetch users for the selected branches, departments, and subdepartments
        const users = await fetchUsersByFilters(branchIds, validDepartmentIds, validSubDepartmentIds);
        setFilteredAssignees(users);
        setFilteredObservers(users);
        setFilteredParticipants(users);
        
        // Filter selected assignees, observers, and participants to keep only valid ones
        const userIds = users.map(u => u.id);
        const validAssignees = currentAssignees.filter(a => userIds.includes(a.id));
        const validObservers = currentObservers.filter(o => userIds.includes(o.id));
        const validParticipants = currentParticipants.filter(p => userIds.includes(p.id));
        
        setFieldValue('assigneeList', validAssignees);
        setFieldValue('Observers', validObservers);
        setFieldValue('Participants', validParticipants);
      } catch (error) {
        console.error('Error fetching departments/sub-departments by branch:', error);
        // Fallback to filtering from existing data if API call fails
        const filtered = departments.filter(dept => branchIds.includes(dept.branch_id));
        setFilteredDepartments(filtered);
        const relatedDepartmentIds = filtered.map(d => d.id);
        
        const validDepartmentIds = currentDepartmentIds.filter(deptId => relatedDepartmentIds.includes(deptId));
        setFieldValue('DepartmentId', validDepartmentIds);
        
        const subsForBranches = subDepartments.filter(sd => relatedDepartmentIds.includes(sd.department_id));
        setFilteredSubDepartments(subsForBranches);
        
        const validSubDepartmentIds = currentSubDepartmentIds.filter(subDeptId => {
          const subDept = subDepartments.find(sd => sd.id === subDeptId);
          return subDept && validDepartmentIds.includes(subDept.department_id);
        });
        setFieldValue('SubDepartmentId', validSubDepartmentIds);
        
        const users = await fetchUsersByFilters(branchIds, validDepartmentIds, validSubDepartmentIds);
        setFilteredAssignees(users);
        setFilteredObservers(users);
        setFilteredParticipants(users);
        
        const userIds = users.map(u => u.id);
        const validAssignees = currentAssignees.filter(a => userIds.includes(a.id));
        const validObservers = currentObservers.filter(o => userIds.includes(o.id));
        const validParticipants = currentParticipants.filter(p => userIds.includes(p.id));
        
        setFieldValue('assigneeList', validAssignees);
        setFieldValue('Observers', validObservers);
        setFieldValue('Participants', validParticipants);
      }
    } else {
      // No branches selected - reset everything
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
      setFieldValue('DepartmentId', []);
      setFieldValue('SubDepartmentId', []);
      // Reset to original lists when no branch selected
      setFilteredAssignees(availableAssignees);
      setFilteredParticipants(participants);
    }
  };

  // Handle department change
  const handleDepartmentChange = async (selectedDepartments, setFieldValue, branchIds, currentSubDepartmentIds = [], currentAssignees = [], currentObservers = [], currentParticipants = []) => {
    const departmentIds = selectedDepartments ? selectedDepartments.map(option => option.value) : [];
    setFieldValue('DepartmentId', departmentIds);
    
    if (departmentIds.length > 0) {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        
        // Call API to fetch sub-departments filtered by selected departments and branches (if available)
        // Build API calls for each department, and include branch filter if branches are selected
        let subDepartmentPromises = [];
        
        if (branchIds.length > 0) {
          // If branches are selected, make calls for each branch-department combination
          // to ensure we get all sub-departments that match any of the selected branches
          departmentIds.forEach(departmentId => {
            branchIds.forEach(branchId => {
              let url = `/settings/subDepartments?corporate_id=${corporateId}&department_id=${departmentId}&branch_id=${branchId}`;
              // Try camel-case endpoint first, then fallback to lowercase
              subDepartmentPromises.push(
                axiosInstance.get(url)
                  .catch(() => {
                    const lowerUrl = url.replace('/settings/subDepartments', '/settings/subdepartments');
                    return axiosInstance.get(lowerUrl);
                  })
              );
            });
          });
        } else {
          // No branches selected, just filter by department - one call per department
          departmentIds.forEach(departmentId => {
            let url = `/settings/subDepartments?corporate_id=${corporateId}&department_id=${departmentId}`;
            // Try camel-case endpoint first, then fallback to lowercase
            subDepartmentPromises.push(
              axiosInstance.get(url)
                .catch(() => {
                  const lowerUrl = url.replace('/settings/subDepartments', '/settings/subdepartments');
                  return axiosInstance.get(lowerUrl);
                })
            );
          });
        }
        
        const subDepartmentResponses = await Promise.all(subDepartmentPromises);
        
        // Combine all sub-departments and remove duplicates
        const allSubDepartments = [];
        subDepartmentResponses.forEach(res => {
          const data = res.data;
          const subDepts = data.data || data || [];
          // Remove duplicates based on id
          subDepts.forEach(subDept => {
            if (!allSubDepartments.find(sd => sd.id === subDept.id)) {
              allSubDepartments.push(subDept);
            }
          });
        });
        
        // Filter to only include sub-departments that belong to selected departments
        const subs = allSubDepartments.filter(sd => departmentIds.includes(sd.department_id));
        setFilteredSubDepartments(subs);
        
        // Keep only valid subdepartments (that still belong to selected departments)
        const validSubDepartmentIds = currentSubDepartmentIds.filter(subDeptId => {
          const subDept = subs.find(sd => sd.id === subDeptId);
          return subDept && departmentIds.includes(subDept.department_id);
        });
        setFieldValue('SubDepartmentId', validSubDepartmentIds);
        
        // Call API to fetch users for the selected branches, departments, and subdepartments
        const users = await fetchUsersByFilters(branchIds, departmentIds, validSubDepartmentIds);
        setFilteredAssignees(users);
        setFilteredObservers(users);
        setFilteredParticipants(users);
        
        // Filter selected assignees, observers, and participants to keep only valid ones
        const userIds = users.map(u => u.id);
        const validAssignees = currentAssignees.filter(a => userIds.includes(a.id));
        const validObservers = currentObservers.filter(o => userIds.includes(o.id));
        const validParticipants = currentParticipants.filter(p => userIds.includes(p.id));
        
        setFieldValue('assigneeList', validAssignees);
        setFieldValue('Observers', validObservers);
        setFieldValue('Participants', validParticipants);
      } catch (error) {
        console.error('Error fetching sub-departments by department/branch:', error);
        // Fallback to filtering from existing data if API call fails
        const subs = subDepartments.filter(sd => departmentIds.includes(sd.department_id));
        setFilteredSubDepartments(subs);
        
        const validSubDepartmentIds = currentSubDepartmentIds.filter(subDeptId => {
          const subDept = subDepartments.find(sd => sd.id === subDeptId);
          return subDept && departmentIds.includes(subDept.department_id);
        });
        setFieldValue('SubDepartmentId', validSubDepartmentIds);
        
        const users = await fetchUsersByFilters(branchIds, departmentIds, validSubDepartmentIds);
        setFilteredAssignees(users);
        setFilteredObservers(users);
        setFilteredParticipants(users);
        
        const userIds = users.map(u => u.id);
        const validAssignees = currentAssignees.filter(a => userIds.includes(a.id));
        const validObservers = currentObservers.filter(o => userIds.includes(o.id));
        const validParticipants = currentParticipants.filter(p => userIds.includes(p.id));
        
        setFieldValue('assigneeList', validAssignees);
        setFieldValue('Observers', validObservers);
        setFieldValue('Participants', validParticipants);
      }
    } else {
      // No departments selected - reset subdepartments
      setFilteredSubDepartments([]);
      setFieldValue('SubDepartmentId', []);
      
      if (branchIds.length > 0) {
        // If no department selected but branches are selected, call API for branches only
        const users = await fetchUsersByFilters(branchIds, [], []);
        setFilteredAssignees(users);
        setFilteredParticipants(users);
        
        // Filter selected assignees, observers, and participants
        const userIds = users.map(u => u.id);
        const validAssignees = currentAssignees.filter(a => userIds.includes(a.id));
        const validObservers = currentObservers.filter(o => userIds.includes(o.id));
        const validParticipants = currentParticipants.filter(p => userIds.includes(p.id));
        
        setFieldValue('assigneeList', validAssignees);
        setFieldValue('Observers', validObservers);
        setFieldValue('Participants', validParticipants);
      } else {
        const users = await fetchUsersByFilters([], [], []);
        setFilteredAssignees(users);
        setFilteredObservers(users);
        setFilteredParticipants(users);
        
        // Filter selected assignees, observers, and participants
        const userIds = users.map(u => u.id);
        const validAssignees = currentAssignees.filter(a => userIds.includes(a.id));
        const validObservers = currentObservers.filter(o => userIds.includes(o.id));
        const validParticipants = currentParticipants.filter(p => userIds.includes(p.id));
        
        setFieldValue('assigneeList', validAssignees);
        setFieldValue('Observers', validObservers);
        setFieldValue('Participants', validParticipants);
      }
    }
  };

  const handleSubDepartmentChange = async (selectedSubDepartments, setFieldValue, branchIds, departmentIds, currentAssignees = [], currentObservers = [], currentParticipants = []) => {
    const subDepartmentIds = selectedSubDepartments ? selectedSubDepartments.map(option => option.value) : [];
    setFieldValue('SubDepartmentId', subDepartmentIds);
    
    let users = [];
    
    // If subdepartments are selected, filter by them
    // If cleared, fall back to department filtering
    if (subDepartmentIds.length > 0) {
      users = await fetchUsersByFilters(branchIds, departmentIds, subDepartmentIds);
      setFilteredAssignees(users);
      setFilteredParticipants(users);
    } else if (departmentIds.length > 0) {
      // If no subdepartment selected but departments are selected, filter by departments
      users = await fetchUsersByFilters(branchIds, departmentIds, []);
      setFilteredAssignees(users);
      setFilteredParticipants(users);
    } else if (branchIds.length > 0) {
      // If no department selected but branches are selected, filter by branches only
      users = await fetchUsersByFilters(branchIds, [], []);
      setFilteredAssignees(users);
      setFilteredParticipants(users);
    } else {
      // No filters selected, show all users
      users = availableAssignees;
      setFilteredAssignees(availableAssignees);
      setFilteredParticipants(participants);
    }
    
    // Filter selected assignees, observers, and participants to keep only valid ones
    const userIds = users.map(u => u.id);
    const validAssignees = currentAssignees.filter(a => userIds.includes(a.id));
    const validObservers = currentObservers.filter(o => userIds.includes(o.id));
    const validParticipants = currentParticipants.filter(p => userIds.includes(p.id));
    
    setFieldValue('assigneeList', validAssignees);
    setFieldValue('Observers', validObservers);
    setFieldValue('Participants', validParticipants);
  };


  // Update filtered departments when departments prop changes
  useEffect(() => {
    setFilteredDepartments(departments);
  }, [departments]);

  useEffect(() => {
    setFilteredSubDepartments(subDepartments);
  }, [subDepartments]);

  // Update filtered lists when props change (skip for forum prefill — org filters manage the lists)
  useEffect(() => {
    if (prefillForumData) return;
    setFilteredAssignees(availableAssignees);
    setFilteredObservers(observers);
    setFilteredParticipants(participants);
  }, [availableAssignees, observers, participants, prefillForumData]);

  // Handle initial load for edit mode or add-from-project with existing branch/department
  useEffect(() => {
    const loadInitialFilteredUsers = async () => {
      if (mode === 'edit' && initialData) {
        const branchIds = initialData?.branches ? initialData.branches.map(branch => branch.id) : (initialData?.Branch?.id ? [initialData.Branch.id] : []);
        const departmentIds = initialData?.departments ? initialData.departments.map(department => department.id) : (initialData?.Department?.id ? [initialData.Department.id] : []);
        const subDepartmentIds = initialData?.subDepartments ? initialData.subDepartments.map(sd => sd.sub_department_id) : (initialData?.SubDepartment?.id ? [initialData.SubDepartment.id] : []);
        
        if (branchIds.length > 0) {
          // Filter departments for the existing branches
          const filtered = departments.filter(dept => branchIds.includes(dept.branch_id));
          setFilteredDepartments(filtered);
          const subs = subDepartments.filter(sd => departmentIds.includes(sd.department_id));
          setFilteredSubDepartments(subs);
          
          // Fetch users for the existing branches and departments
          const users = await fetchUsersByFilters(branchIds, departmentIds, subDepartmentIds);
          setFilteredAssignees(users);
          setFilteredObservers(users);
          setFilteredParticipants(users);
        }
      } else if (mode === 'add' && prefillProjectData) {
        const branchIds = prefillProjectData.BranchId || prefillProjectData.branchIds || [];
        const departmentIds = prefillProjectData.DepartmentId || prefillProjectData.departmentIds || [];
        const subDepartmentIds = prefillProjectData.SubDepartmentId || prefillProjectData.subDepartmentIds || [];
        if (branchIds.length > 0) {
          const filtered = departments.filter(dept => branchIds.includes(dept.branch_id));
          setFilteredDepartments(filtered);
          const subs = subDepartments.filter(sd => departmentIds.includes(sd.department_id));
          setFilteredSubDepartments(subs);
          const users = await fetchUsersByFilters(branchIds, departmentIds, subDepartmentIds);
          setFilteredAssignees(users);
          setFilteredObservers(users);
          setFilteredParticipants(users);
        }
      } else if (mode === 'add' && prefillForumData) {
        const branchIds = prefillForumData.BranchId || [];
        const departmentIds = prefillForumData.DepartmentId || [];
        const subDepartmentIds = prefillForumData.SubDepartmentId || [];
        if (branchIds.length > 0) {
          const filtered = departments.filter(dept =>
            branchIds.some((branchId) => idsMatch(branchId, dept.branch_id))
          );
          setFilteredDepartments(filtered);
        }
        if (departmentIds.length > 0) {
          const subs = subDepartments.filter(sd =>
            departmentIds.some((deptId) => idsMatch(deptId, sd.department_id))
          );
          setFilteredSubDepartments(subs);
        }
        if (!forumPrefillUsersLoadedRef.current) {
          forumPrefillUsersLoadedRef.current = true;
          if (branchIds.length > 0 || departmentIds.length > 0 || subDepartmentIds.length > 0) {
            const users = await fetchUsersByFilters(branchIds, departmentIds, subDepartmentIds);
            const prefilledAssignees = prefillForumData.assignees || [];
            const mergedUsers = [...users];
            prefilledAssignees.forEach((assignee) => {
              if (assignee?.id && !mergedUsers.some((u) => idsMatch(u.id, assignee.id))) {
                mergedUsers.push(assignee);
              }
            });
            setFilteredAssignees(mergedUsers);
            setFilteredObservers(mergedUsers);
            setFilteredParticipants(mergedUsers);
          } else if ((prefillForumData.assignees || []).length > 0) {
            setFilteredAssignees(prefillForumData.assignees);
            setFilteredObservers(prefillForumData.assignees);
            setFilteredParticipants(prefillForumData.assignees);
          }
        }
      }
    };

    loadInitialFilteredUsers();
  }, [mode, initialData, prefillProjectData, prefillForumData, departments, subDepartments]);

  // Fetch tasks for the selected date when StartDate === Deadline
  const fetchDayTasks = async (startDate, assigneeIds = []) => {
    if (!startDate) {
      setDayTasks([]);
      return;
    }

    try {
      setDayTasksLoading(true);
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.corporateId) {
        setDayTasks([]);
        return;
      }

      const params = new URLSearchParams({
        CorporateId: userData.corporateId,
        start_date: startDate,
        end_date: startDate,
        page: 1,
        limit: 100
      });

      // Filter by assignees if provided
      // Backend accepts comma-separated format: corporate_user=123,456,789
      if (assigneeIds.length > 0) {
        params.append('corporate_user', assigneeIds.join(','));
      }

      const response = await axiosInstance.get(`/task/list?${params.toString()}`);
      const result = response.data;

      if (result.tasks) {
        // Transform tasks to match CommonTable format
        const formattedTasks = result.tasks.map(task => ({
          id: task.id,
          TaskName: task.title || task.TaskName,
          Priority: task.priority || task.Priority,
          Status: task.status !== undefined ? task.status : (task.Status !== undefined ? task.Status : 0),
          StartDate: task.start_date ? moment(task.start_date).format('YYYY-MM-DD') : '',
          EndDate: task.due_date ? moment(task.due_date).format('YYYY-MM-DD') : '',
          StartTime: task.start_date && (task.start_date.includes(' ') || task.start_date.includes('T')) 
            ? task.start_date.split(/[\sT]/)[1]?.substring(0, 5) || '00:00'
            : '00:00',
          EndTime: task.due_date && (task.due_date.includes(' ') || task.due_date.includes('T'))
            ? task.due_date.split(/[\sT]/)[1]?.substring(0, 5) || '00:00'
            : '00:00',
          Assignees: task.Assignees || task.assignee_list || []
        }));
        setDayTasks(formattedTasks);
      } else {
        setDayTasks([]);
      }
    } catch (error) {
      console.error('Error fetching day tasks:', error);
      setDayTasks([]);
    } finally {
      setDayTasksLoading(false);
    }
  };

  // Ref to track previous values for useEffect
  const prevValuesRef = useRef({ StartDate: null, assigneeIdsStr: '' });
  
  // Helper function to get assignee IDs from assignee list
  const getAssigneeIds = (assigneeList) => {
    return assigneeList?.map(a => a.id || a.CorporateUserId).filter(Boolean) || [];
  };

  const validationSchema = Yup.object({
    TaskName: Yup.string()
      .required('Task Name is required.')
      .test('no-only-special-chars', 'Task name cannot contain only special characters.', (value) => {
        if (!value || !String(value).trim()) return true;
        return /[a-zA-Z0-9]/.test(String(value).trim());
      }),
    Deadline: Yup.string()
      .when('IsRepeatable', {
        is: false,
        then: (schema) => schema.required('End Date is required.'),
        otherwise: (schema) => schema.nullable()
      })
      .test('deadline-after-start', 'End Date must be after Start Date.', function (value) {
        const { StartDate, IsRepeatable } = this.parent;
        if (IsRepeatable || !value || !StartDate) return true;
        const deadlineDate = new Date(value + 'T00:00:00');
        const startDate = new Date(StartDate + 'T00:00:00');
        return deadlineDate >= startDate;
      }),
    EndTime: Yup.string()
      .test('end-time-after-start', 'End Time must be after Start Time.', function (value) {
        const { StartDate, Deadline, StartTime, IsRepeatable } = this.parent;
        if (IsRepeatable || !StartDate || !Deadline || StartDate !== Deadline) return true;
        if (!value || !StartTime) return true;
        const startMinutes = parse12HourToMinutes(StartTime);
        const endMinutes = parse12HourToMinutes(value);
        return endMinutes > startMinutes;
      }),
    StartDate: Yup.string()
      .required('Start Date is required.'),
    RepeatType: Yup.string()
      .when('IsRepeatable', {
        is: true,
        then: (schema) => schema.required('Repeat Type is required when task is repeatable.'),
        otherwise: (schema) => schema.nullable()
      }),
    RepeatInterval: Yup.string()
      .when('IsRepeatable', {
        is: true,
        then: (schema) => schema.required('Repeat Interval is required when task is repeatable.'),
        otherwise: (schema) => schema.nullable()
      }),
    RepeatUntil: Yup.string()
      .when(['IsRepeatable', 'RepeatNeverEnds'], {
        is: (IsRepeatable, RepeatNeverEnds) => IsRepeatable && !RepeatNeverEnds,
        then: (schema) => schema.required('Repeat Until is required when task is repeatable and has an end date.'),
        otherwise: (schema) => schema.nullable()
      })
      .test('repeat-until-after-start', 'Repeat Until must be after Start Date.', function (value) {
        const { StartDate, IsRepeatable, RepeatNeverEnds } = this.parent;
        if (!IsRepeatable || !value || !StartDate || RepeatNeverEnds) return true;
        const repeatDate = new Date(value + 'T00:00:00');
        const startDate = new Date(StartDate + 'T00:00:00');
        return repeatDate >= startDate;
      }),
    WeeklyDays: Yup.array().nullable(),
    MonthlyRepeatType: Yup.string().nullable(),
    Email: Yup.string().email('Email is not a valid email.'),
    ReminderEmail: Yup.string().email('ReminderEmail is not a valid email.'),
    TaskStatusEmail: Yup.string().email('TaskStatusEmail is not a valid email.'),
  });

  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '10px',
      borderColor: state.isFocused ? '#198754' : 'rgba(0, 0, 0, 0.125)',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#198754',
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '10px',
      zIndex: 9999,
    }),
  };

  // Checklist helpers
  const checklistInputRefs = useRef({});
  const focusChecklistInput = (id) => {
    setTimeout(() => {
      const el = checklistInputRefs.current[id];
      if (el && typeof el.focus === 'function') {
        el.focus();
        try {
          const value = el.value || '';
          if (typeof el.setSelectionRange === 'function') {
            el.setSelectionRange(value.length, value.length);
          }
        } catch (e) {
          // noop
        }
      }
    }, 0);
  };
  const generateChecklistId = () => Date.now() + Math.random();

  const handleChecklistAddGlobal = (values, setFieldValue) => {
    const current = Array.isArray(values.Checklist) ? [...values.Checklist] : [];
    // If an empty editable row exists, focus it instead of adding another
    const existingEmptyIdx = current.findIndex(item => (item?.value || '').trim() === '' && item.isEditing);
    if (existingEmptyIdx >= 0) {
      const id = current[existingEmptyIdx]?.id;
      if (id) focusChecklistInput(id);
      return;
    }
    const newId = generateChecklistId();
    current.push({ value: '', checked: false, isEditing: true, id: newId });
    setFieldValue('Checklist', current);
    focusChecklistInput(newId);
  };

  const handleChecklistChange = (setFieldValue, index, value) => {
    setFieldValue(`Checklist[${index}].value`, value);
  };

  const handleChecklistCheck = (setFieldValue, index, checked) => {
    setFieldValue(`Checklist[${index}].checked`, checked);
  };

  const handleChecklistAdd = (values, setFieldValue, index) => {
    const updatedChecklist = [...values.Checklist];
    updatedChecklist[index].isEditing = false;
    const newId = generateChecklistId();
    updatedChecklist.splice(index + 1, 0, {
      value: '',
      checked: false,
      isEditing: true,
      id: newId
    });
    setFieldValue('Checklist', updatedChecklist);
    focusChecklistInput(newId);
  };

  const handleChecklistEdit = (values, setFieldValue, index) => {
    const updatedChecklist = [...values.Checklist];
    updatedChecklist[index].isEditing = true;
    const id = updatedChecklist[index]?.id;
    setFieldValue('Checklist', updatedChecklist);
    if (id) {
      focusChecklistInput(id);
    }
  };

  const handleChecklistDelete = (values, setFieldValue, index) => {
    const updatedChecklist = values.Checklist.filter((_, i) => i !== index);
    setFieldValue('Checklist', updatedChecklist.length > 0 ? updatedChecklist : [{
      value: '',
      checked: false,
      isEditing: true,
      id: generateChecklistId()
    }]);
  };

  const handleChecklistKeyDown = (e, values, setFieldValue, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentValue = (values.Checklist[index]?.value || '').trim();
      if (!currentValue) {
        // Stay in edit mode if empty to avoid creating multiple blank items
        return;
      }
      // Commit current and add a new editable row below, then focus it
      const updatedChecklist = [...values.Checklist];
      updatedChecklist[index].isEditing = false;
      const newId = generateChecklistId();
      updatedChecklist.splice(index + 1, 0, {
        value: '',
        checked: false,
        isEditing: true,
        id: newId
      });
      setFieldValue('Checklist', updatedChecklist);
      focusChecklistInput(newId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      const updatedChecklist = [...values.Checklist];
      updatedChecklist[index].isEditing = false;
      setFieldValue('Checklist', updatedChecklist);
    }
  };

  const handleChecklistBlur = (values, setFieldValue, index) => {
    // Auto-append a blank editable row if user filled the last item
    const isLast = index === values.Checklist.length - 1;
    const hasValue = (values.Checklist[index]?.value || '').trim() !== '';
    if (isLast && hasValue) {
      const newId = generateChecklistId();
      const updated = [...values.Checklist, { value: '', checked: false, isEditing: true, id: newId }];
      setFieldValue('Checklist', updated);
    }
  };

  const handleFileChange = (e, values, setFieldValue) => {
    const newFiles = Array.from(e.target.files);
    setFieldValue('Files', [...values.Files, ...newFiles]);
  };

  const handleRemoveFile = (index, values, setFieldValue) => {
    const updatedFiles = values.Files.filter((_, i) => i !== index);
    setFieldValue('Files', updatedFiles);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreview = async (file) => {
    if (file?.size === undefined) {
      if (file?.id) {
        try {
          const blob = await fetchTaskAttachmentBlob(file.id);
          const fileUrl = URL.createObjectURL(blob);
          setFilePreview({ file, url: fileUrl, isBlob: true });
          setShowPreviewModal(true);
        } catch (e) {
          console.error(e);
          toast.error(e.message || 'Could not load preview');
        }
        return;
      }
      toast.error('Attachment is missing an id. Refresh and try again.');
      return;
    }
    const fileUrl = URL.createObjectURL(file);
    const extension = file.file_name.split('.').pop().toLowerCase();
    setFilePreview({
      file: { FileName: file.name, FileExtension: extension },
      url: fileUrl,
      isBlob: true,
    });
    setShowPreviewModal(true);
  };

  const extractMentionsAndCleanDescription = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const mentions = [];
    
    // Handle mentions - replace with plain text but preserve structure
    const mentionSpans = div.querySelectorAll('span.mention');
    mentionSpans.forEach((span) => {
      const name = span.innerText || span.textContent;
      const id = span.dataset.id;
      mentions.push({ id, name });
      // Replace with a text node to preserve list structure
      const textNode = document.createTextNode(name);
      span.parentNode.replaceChild(textNode, span);
    });
    
    // Remove cursor spans
    const cursorSpans = div.querySelectorAll('span.ql-cursor');
    cursorSpans.forEach(span => span.remove());
    
    // Clean up any empty list items that might have been created
    const emptyListItems = div.querySelectorAll('li:empty');
    emptyListItems.forEach(li => {
      // Only remove if it's truly empty (no text content)
      if (!li.textContent.trim()) {
        li.remove();
      }
    });
    
    // Clean up empty lists
    const emptyLists = div.querySelectorAll('ul:empty, ol:empty');
    emptyLists.forEach(list => list.remove());
    
    const cleanedDescription = div.innerHTML.trim();
    const plainText = div.textContent || div.innerText || '';
    return {
      cleanedDescription,
      plainText,
      mentions
    };
  };

  const handleChangeDescription = (description, mentions, delta) => {
    const { cleanedDescription } = extractMentionsAndCleanDescription(description);

    return {
      description: cleanedDescription || description,
      mentions,
      delta: delta || null, // Ensure delta is null if not provided
    };
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const sanitizedChecklist = Array.isArray(values.Checklist)
        ? values.Checklist.filter(item => (item?.value || '').trim() !== '')
        : [];
      const payload = { ...values, Checklist: sanitizedChecklist };
      await onSubmit(payload, setSubmitting);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred while saving the task');
    }
  };

  const handleOpenInNewTab = () => {
    const url = filePreview.url;

    if (!url) {
      alert('File URL is missing.');
      return;
    }

    window.open(url, '_blank');
  };


  return (
    <div className="row">
      <ToastContainer />
      <div className="col-xl-12 col-xxl-12">
        <div className="card">
          <div className="card-body">
            <section>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={handleFormSubmit}
              >
                {({ values, setFieldValue, isSubmitting, handleChange }) => (
                  <Form>
                    <div className="row">
                      {/* TaskName */}
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">Task Name*</label>
                        <Field
                          type="text"
                          className="form-control"
                          name="TaskName"
                          autoComplete="off"
                          onChange={handleChange}
                        />
                        <ErrorMessage name="TaskName" component="div" className="text-danger" />
                      </div>
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">Priority</label>
                        <Field as="select" className="form-control" name="Priority">
                          <option value="">Select</option>
                          <option>Low</option>
                          <option>Medium</option>
                          <option>High</option>
                        </Field>
                        <ErrorMessage name="Priority" component="div" className="text-danger" />
                      </div>


                      {/* Description */}


                      {mode === 'edit' ? (
                        <MentionEditorEdit
                          users={filteredAssignees}
                          description={values.Description || Description}
                          delta={values.Delta || delta}
                          onChange={(data) => {
                            const decriprion_value = handleChangeDescription(data.description, data.mentions, data.delta)
                            setFieldValue("Description", decriprion_value.description)
                            setFieldValue("Delta", JSON.stringify(decriprion_value.delta))
                            setFieldValue("Mentions", decriprion_value.mentions)
                          }}
                        />
                      ) : (
                        <MentionEditorAdd
                          users={filteredAssignees}
                          description={values.Description || Description}
                          delta={values.Delta || delta}
                          onChange={(data) => {
                            const decriprion_value = handleChangeDescription(data.description, data.mentions, data.delta)
                            setFieldValue("Description", decriprion_value.description)
                            setFieldValue("Delta", JSON.stringify(decriprion_value.delta))
                            setFieldValue("Mentions", decriprion_value.mentions)
                          }}
                        />
                      )}

                      {/* StartDate */}
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">Start Date *</label>
                        <Field
                          name="StartDate"
                          render={({ field, form }) => {
                            const { setFieldValue } = form;
                            return (
                              <DateInput
                                type="date"
                                className="form-control"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  // If IsRepeatable is true, sync Deadline with StartDate
                                  if (values.IsRepeatable && e.target.value) {
                                    setFieldValue('Deadline', e.target.value);
                                  }
                                }}
                              />
                            );
                          }}
                        />
                        <ErrorMessage name="StartDate" component="div" className="text-danger" />
                      </div>

                      {/* End Date - Hide when IsRepeatable is true */}
                      {!values.IsRepeatable && (
                        <div className="col-lg-6 mb-2">
                          <label className="text-label">End Date *</label>
                          <Field
                            name="Deadline"
                            render={({ field }) => (
                              <DateInput
                                type="date"
                                className="form-control"
                                {...field}
                              />
                            )}
                          />
                          <ErrorMessage name="Deadline" component="div" className="text-danger" />
                        </div>
                      )}
                      {/* When IsRepeatable is true, keep Deadline in sync with StartDate but hidden */}
                      {values.IsRepeatable && (
                        <Field
                          name="Deadline"
                          render={({ field }) => {
                            // Hidden input to maintain form state - value synced by onChange handlers
                            return <input type="hidden" {...field} />;
                          }}
                        />
                      )}

                      {/* Start Time and End Time - Only show when StartDate === Deadline and IsRepeatable is false */}
                      {!values.IsRepeatable && values.StartDate && values.Deadline && values.StartDate === values.Deadline && (
                        <>
                          <div className="col-lg-6 mb-2">
                            <label className="text-label">Start Time</label>
                            <Field
                              name="StartTime"
                              render={({ field, form }) => {
                                const { setFieldValue } = form;
                                const time12 = field.value || '12:00 PM';
                                const parsedTime = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
                                const displayHour = parsedTime ? parsedTime[1] : '12';
                                const displayMinute = parsedTime ? parsedTime[2] : '00';
                                const displayAmpm = parsedTime ? parsedTime[3].toUpperCase() : 'PM';
                                
                                return (
                                  <div className="d-flex align-items-center gap-2">
                                    <select
                                      className="form-control"
                                      style={{ width: '80px', height: '40px' }}
                                      value={displayHour}
                                      onChange={(e) => {
                                        const newTime = `${e.target.value}:${displayMinute} ${displayAmpm}`;
                                        setFieldValue('StartTime', newTime);
                                      }}
                                    >
                                      {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                        <option key={h} value={h}>{h}</option>
                                      ))}
                                    </select>
                                    <span>:</span>
                                    <select
                                      className="form-control"
                                      style={{ width: '80px', height: '40px' }}
                                      value={displayMinute}
                                      onChange={(e) => {
                                        const newTime = `${displayHour}:${e.target.value.padStart(2, '0')} ${displayAmpm}`;
                                        setFieldValue('StartTime', newTime);
                                      }}
                                    >
                                      {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
                                        <option key={m} value={m}>{m}</option>
                                      ))}
                                    </select>
                                    <select
                                      className="form-control"
                                      style={{ width: '80px', height: '40px' }}
                                      value={displayAmpm}
                                      onChange={(e) => {
                                        const newTime = `${displayHour}:${displayMinute} ${e.target.value}`;
                                        setFieldValue('StartTime', newTime);
                                      }}
                                    >
                                      <option value="AM">AM</option>
                                      <option value="PM">PM</option>
                                    </select>
                                  </div>
                                );
                              }}
                            />
                            <ErrorMessage name="StartTime" component="div" className="text-danger" />
                          </div>
                          <div className="col-lg-6 mb-2">
                            <label className="text-label">End Time</label>
                            <Field
                              name="EndTime"
                              render={({ field, form }) => {
                                const { setFieldValue } = form;
                                const time12 = field.value || '12:00 PM';
                                const parsedTime = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
                                const displayHour = parsedTime ? parsedTime[1] : '12';
                                const displayMinute = parsedTime ? parsedTime[2] : '00';
                                const displayAmpm = parsedTime ? parsedTime[3].toUpperCase() : 'PM';
                                
                                return (
                                  <div className="d-flex align-items-center gap-2">
                                    <select
                                      className="form-control"
                                      style={{ width: '80px', height: '40px' }}
                                      value={displayHour}
                                      onChange={(e) => {
                                        const newTime = `${e.target.value}:${displayMinute} ${displayAmpm}`;
                                        setFieldValue('EndTime', newTime);
                                      }}
                                    >
                                      {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                        <option key={h} value={h}>{h}</option>
                                      ))}
                                    </select>
                                    <span>:</span>
                                    <select
                                      className="form-control"
                                      style={{ width: '80px', height: '40px' }}
                                      value={displayMinute}
                                      onChange={(e) => {
                                        const newTime = `${displayHour}:${e.target.value.padStart(2, '0')} ${displayAmpm}`;
                                        setFieldValue('EndTime', newTime);
                                      }}
                                    >
                                      {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
                                        <option key={m} value={m}>{m}</option>
                                      ))}
                                    </select>
                                    <select
                                      className="form-control"
                                      style={{ width: '80px', height: '40px' }}
                                      value={displayAmpm}
                                      onChange={(e) => {
                                        const newTime = `${displayHour}:${displayMinute} ${e.target.value}`;
                                        setFieldValue('EndTime', newTime);
                                      }}
                                    >
                                      <option value="AM">AM</option>
                                      <option value="PM">PM</option>
                                    </select>
                                  </div>
                                );
                              }}
                            />
                            <ErrorMessage name="EndTime" component="div" className="text-danger" />
                          </div>
                        </>
                      )}

                      {/* Is Repeatable */}
                      <div className='row mt-2 pt-2' >
                        <div className='col-lg-12 mb-2'>
                          {hasRepetitiveTaskAccess ? (
                            <>
                              <Field
                                id="IsRepeatable"
                                type="checkbox"
                                className="form-check-input"
                                name="IsRepeatable"
                                checked={values.IsRepeatable}
                                onChange={e => {
                                  const isChecked = e.target.checked;
                                  setFieldValue('IsRepeatable', isChecked);
                                  // If IsRepeatable is selected, set Deadline to StartDate
                                  if (isChecked && values.StartDate) {
                                    setFieldValue('Deadline', values.StartDate);
                                  }
                                }}
                              />
                              <label htmlFor="IsRepeatable" className="form-check-label">
                                Is Repeatable
                                <i 
                                  className="fas fa-info-circle ms-2 text-muted" 
                                  style={{ fontSize: '0.875rem' }}
                                  title="Enable this to make the task repeat automatically based on the specified interval"
                                ></i>
                              </label>
                            </>
                          ) : (
                            <div className="feature-locked-field">
                              <div className="locked-field-content">
                                <i className="fas fa-lock text-muted me-2"></i>
                                <span className="text-muted">Repetitive Tasks not available in your plan</span>
                              </div>
                            </div>
                          )}
                        </div>
                        {hasRepetitiveTaskAccess && values.IsRepeatable && (
                          <div className="col-12 mb-3 p-3" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', background: '#f9f9f9' }}>
                            <div className="row">
                              {/* Repeat pattern (Interval + Type) */}
                              <div className="col-lg-6 mb-2">
                                <label className="text-label">
                                  Repeat pattern*
                                  <i 
                                    className="fas fa-info-circle ms-2 text-muted" 
                                    style={{ fontSize: '0.875rem' }}
                                    title="Count: How many units (days, weeks, or months) between repetitions. Unit: The time unit for repetition (Daily, Weekly, or Monthly)"
                                  ></i>
                                </label>
                                <div className="d-flex" style={{ gap: '8px' }}>
                                  <Field
                                    type="number"
                                    className="form-control"
                                    name="RepeatInterval"
                                    autoComplete='off'
                                    placeholder="e.g., 2"
                                    style={{ maxWidth: '120px' }}
                                  />
                                  <Field as="select" className="form-control" name="RepeatType" style={{ maxWidth: '200px' }}>
                                    <option value="">Select unit</option>
                                    <option value="Daily">Day</option>
                                    <option value="Weekly">Week</option>
                                    <option value="Monthly">Month</option>
                                  </Field>
                                </div>
                                <ErrorMessage name="RepeatInterval" component="div" className="text-danger" />
                                <ErrorMessage name="RepeatType" component="div" className="text-danger" />
                              </div>
                              {/* RepeatUntil and Repeat Never Ends in same row and column */}
                              <div className="col-lg-6 mb-2">
                                <label className="text-label">
                                  Repeat Until{!values.RepeatNeverEnds ? '*' : ''}
                                  <i 
                                    className="fas fa-info-circle ms-2 text-muted" 
                                    style={{ fontSize: '0.875rem' }}
                                    title="The last date when the task will be automatically repeated"
                                  ></i>
                                </label>
                                <div className="d-flex align-items-center gap-3">
                                  <div style={{ flex: 1 }}>
                                    <Field
                                      name="RepeatUntil"
                                      render={({ field }) => (
                                        <DateInput
                                          type="date"
                                          className="form-control"
                                          autoComplete='off'
                                          disabled={values.RepeatNeverEnds}
                                          {...field}
                                        />
                                      )}
                                    />
                                    <ErrorMessage name="RepeatUntil" component="div" className="text-danger" />
                                  </div>
                                  {/* Repeat Never Ends Checkbox */}
                                  <div className="form-check" style={{ marginTop: '1.5rem' }}>
                                    <Field
                                      id="RepeatNeverEnds"
                                      type="checkbox"
                                      className="form-check-input"
                                      name="RepeatNeverEnds"
                                      checked={values.RepeatNeverEnds}
                                      onChange={e => {
                                        setFieldValue('RepeatNeverEnds', e.target.checked);
                                        if (e.target.checked) {
                                          setFieldValue('RepeatUntil', '');
                                        }
                                      }}
                                    />
                                    <label htmlFor="RepeatNeverEnds" className="form-check-label">
                                      Repeat Never Ends
                                      <i 
                                        className="fas fa-info-circle ms-2 text-muted" 
                                        style={{ fontSize: '0.875rem' }}
                                        title="Check this if the task should repeat indefinitely without an end date"
                                      ></i>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Weekly Days Selection - Modern Style */}
                            {values.RepeatType === 'Weekly' && (
                              <div className="row mt-3">
                                <div className="col-12">
                                  <label className="text-label mb-3 d-block">
                                    Select Days of Week
                                    <i 
                                      className="fas fa-info-circle ms-2 text-muted" 
                                      style={{ fontSize: '0.875rem' }}
                                      title="Select which days of the week the task should repeat"
                                    ></i>
                                  </label>
                                  <div 
                                    className="d-flex flex-wrap justify-content-start" 
                                    style={{ 
                                      gap: '8px', 
                                      marginTop: '12px',
                                      padding: '16px',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '12px',
                                      border: '1px solid #e9ecef'
                                    }}
                                  >
                                    {[
                                      { value: 0, label: 'Sun', fullLabel: 'Sunday' },
                                      { value: 1, label: 'Mon', fullLabel: 'Monday' },
                                      { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
                                      { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
                                      { value: 4, label: 'Thu', fullLabel: 'Thursday' },
                                      { value: 5, label: 'Fri', fullLabel: 'Friday' },
                                      { value: 6, label: 'Sat', fullLabel: 'Saturday' }
                                    ].map(day => (
                                      <button
                                        key={day.value}
                                        type="button"
                                        className={`btn day-pill ${values.WeeklyDays.includes(day.value) ? 'selected' : ''}`}
                                        onClick={() => {
                                          const newDays = values.WeeklyDays.includes(day.value)
                                            ? values.WeeklyDays.filter(d => d !== day.value)
                                            : [...values.WeeklyDays, day.value];
                                          setFieldValue('WeeklyDays', newDays);
                                        }}
                                        style={{
                                          width: '48px',
                                          height: '48px',
                                          borderRadius: '24px',
                                          border: '2px solid #dee2e6',
                                          backgroundColor: values.WeeklyDays.includes(day.value) ? '#dc3545' : '#ffffff',
                                          color: values.WeeklyDays.includes(day.value) ? '#ffffff' : '#495057',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease-in-out',
                                          boxShadow: values.WeeklyDays.includes(day.value) ? '0 2px 8px rgba(220, 53, 69, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                          position: 'relative'
                                        }}
                                        title={day.fullLabel}
                                        onMouseEnter={(e) => {
                                          if (!values.WeeklyDays.includes(day.value)) {
                                            e.target.style.borderColor = '#dc3545';
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!values.WeeklyDays.includes(day.value)) {
                                            e.target.style.borderColor = '#dee2e6';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                          }
                                        }}
                                      >
                                        {day.label}
                                      </button>
                                    ))}
                                  </div>
                                  <ErrorMessage name="WeeklyDays" component="div" className="text-danger mt-2" />
                                </div>
                              </div>
                            )}

                            {/* Monthly Day Selection - Modern Style */}
                            {values.RepeatType === 'Monthly' && (() => {
                              const dateInfo = getDateInfo(values.StartDate);
                              return (
                                <div className="row mt-3">
                                  <div className="col-12">
                                    <label className="text-label mb-3 d-block">
                                      Monthly Repeat Options
                                      <i 
                                        className="fas fa-info-circle ms-2 text-muted" 
                                        style={{ fontSize: '0.875rem' }}
                                        title="Choose how the task should repeat monthly based on start date"
                                      ></i>
                                    </label>
                                    <div 
                                      style={{ 
                                        padding: '20px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '12px',
                                        border: '1px solid #e9ecef'
                                      }}
                                    >
                                      {dateInfo.dayOfMonth && dateInfo.weekdayName ? (
                                        <div className="d-flex gap-3">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="radio"
                                              name="MonthlyRepeatType"
                                              id="monthlyDay"
                                              value="day"
                                              checked={values.MonthlyRepeatType === 'day'}
                                              onChange={(e) => setFieldValue('MonthlyRepeatType', e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="monthlyDay">
                                              Every {dateInfo.dayOfMonth}{getOrdinalSuffix(dateInfo.dayOfMonth)} day of month
                                            </label>
                                          </div>
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="radio"
                                              name="MonthlyRepeatType"
                                              id="monthlyWeekday"
                                              value="weekday"
                                              checked={values.MonthlyRepeatType === 'weekday'}
                                              onChange={(e) => setFieldValue('MonthlyRepeatType', e.target.value)}
                                            />
                                            <label className="form-check-label" htmlFor="monthlyWeekday">
                                              Every {dateInfo.weekOfMonth}{getOrdinalSuffix(dateInfo.weekOfMonth)} {dateInfo.weekdayName} of month
                                            </label>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="text-muted">
                                          <i className="fas fa-info-circle me-2"></i>
                                          Please select a start date to see monthly repeat options
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Branch */}
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">
                          Branch
                          {!hasBranchAccess && (
                            <i className="fas fa-lock text-warning ms-2" title="This feature is locked in your current plan"></i>
                          )}
                        </label>
                        {hasBranchAccess ? (
                          <Select
                            isMulti
                            styles={customStyles}
                            options={branches.map((branch) => ({
                              value: branch.id,
                              label: branch.name,
                              data: branch,
                            }))}
                            value={values.BranchId.map(id => {
                              const branch = branches.find(b => idsMatch(b.id, id));
                              return branch ? { value: branch.id, label: branch.name, data: branch } : null;
                            }).filter(Boolean)}
                            onChange={(selectedOptions) =>
                              handleBranchChange(selectedOptions, setFieldValue, values.DepartmentId, values.SubDepartmentId, values.assigneeList, values.Observers, values.Participants)
                            }
                            isClearable
                            placeholder="Select Branches"
                            isDisabled={isOrgPrefilledFromProject}
                          />
                        ) : (
                          <div className="feature-locked-field">
                            <div className="locked-field-content">
                              <i className="fas fa-lock text-muted me-2"></i>
                              <span className="text-muted">Branch assignment is not available in your current plan</span>
                            </div>
                          </div>
                        )}
                        <ErrorMessage name="BranchId" component="div" className="text-danger" />
                      </div>

                      {/* Department */}
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">
                          Department
                          {!hasDepartmentAccess && (
                            <i className="fas fa-lock text-warning ms-2" title="This feature is locked in your current plan"></i>
                          )}
                        </label>
                        {hasDepartmentAccess ? (
                          <Select
                            isMulti
                            styles={customStyles}
                            options={filteredDepartments.map((department) => ({
                              value: department.id,
                              label: `${department.name} (${department.branch_name})`,
                              data: department,
                            }))}
                            value={values.DepartmentId.map(id => {
                              const department = filteredDepartments.find(d => idsMatch(d.id, id));
                              return department ? { value: department.id, label: department.name, data: department } : null;
                            }).filter(Boolean)}
                            onChange={(selectedOptions) =>
                              handleDepartmentChange(selectedOptions, setFieldValue, values.BranchId, values.SubDepartmentId, values.assigneeList, values.Observers, values.Participants)
                            }
                            isClearable
                            placeholder="Select Departments"
                            isDisabled={isOrgPrefilledFromProject}
                          />
                        ) : (
                          <div className="feature-locked-field">
                            <div className="locked-field-content">
                              <i className="fas fa-lock text-muted me-2"></i>
                              <span className="text-muted">Department assignment is not available in your current plan</span>
                            </div>
                          </div>
                        )}
                        <ErrorMessage name="DepartmentId" component="div" className="text-danger" />
                      </div>

                      {/* Sub-Department */}
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">
                          Sub-Department
                          {!hasDepartmentAccess && (
                            <i className="fas fa-lock text-warning ms-2" title="This feature is locked in your current plan"></i>
                          )}
                        </label>
                        {hasDepartmentAccess ? (
                          <Select
                            isMulti
                            styles={customStyles}
                            options={filteredSubDepartments.map((sd) => ({
                              value: sd.id,
                              label: sd.name,
                              data: sd,
                            }))}
                            value={values.SubDepartmentId.map(id => {
                              const sd = filteredSubDepartments.find(s => idsMatch(s.id, id)) || subDepartments.find(s => idsMatch(s.id, id));
                              return sd ? { value: sd.id, label: sd.name, data: sd } : null;
                            }).filter(Boolean)}
                            onChange={(selectedOptions) =>
                              handleSubDepartmentChange(selectedOptions, setFieldValue, values.BranchId, values.DepartmentId, values.assigneeList, values.Observers, values.Participants)
                            }
                            isClearable
                            placeholder="Select Sub-Departments"
                            isDisabled={isOrgPrefilledFromProject}
                          />
                        ) : (
                          <div className="feature-locked-field">
                            <div className="locked-field-content">
                              <i className="fas fa-lock text-muted me-2"></i>
                              <span className="text-muted">Sub-Department assignment is not available in your current plan</span>
                            </div>
                          </div>
                        )}
                        <ErrorMessage name="SubDepartmentId" component="div" className="text-danger" />
                      </div>

                      {/* Assignees */}
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">
                          Individuals
                          {!hasAssigneeAccess && (
                            <i className="fas fa-lock text-warning ms-2" title="This feature is locked in your current plan"></i>
                          )}
                        </label>
                        {hasAssigneeAccess ? (
                          <Select
                            isMulti
                            styles={customStyles}
                            options={filteredAssignees
                              .map((assignee) => ({
                                value: assignee.id,
                                label: `${assignee.full_name} ${((assignee.department_name || assignee.branch_name) ? `(${assignee.department_name ? assignee.department_name + "," : ''}${assignee.branch_name ? assignee.branch_name : ''})` : '')}`,
                                data: assignee,
                              }))
                            }
                            value={values.assigneeList.map((a) => ({
                              value: a.id,
                              label: a.full_name,
                              data: a,
                            }))}
                            onChange={(selectedOptions) =>
                              setFieldValue(
                                'assigneeList',
                                selectedOptions.map((option) => option.data)
                              )
                            }
                          />
                        ) : (
                          <div className="feature-locked-field">
                            <div className="locked-field-content">
                              <i className="fas fa-lock text-muted me-2"></i>
                              <span className="text-muted">Adding assignees is not available in your current plan</span>
                            </div>
                          </div>
                        )}
                        <ErrorMessage name="assigneeList" component="div" className="text-danger" />
                      </div>

                      {/* Create As Individual */}
                      <div className="col-lg-6 mb-2">
                        <div className="form-check mt-4">
                          <Field
                            type="checkbox"
                            className="form-check-input"
                            name="CreateAsIndividual"
                            id="CreateAsIndividual"
                          />
                          <label className="form-check-label" htmlFor="CreateAsIndividual">
                            Create As Individual
                            <i 
                              className="fas fa-info-circle ms-2 text-muted" 
                              style={{ fontSize: '0.875rem' }}
                              title="Enable this to create the task as an individual task rather than a team task"
                            ></i>
                          </label>
                        </div>
                        <ErrorMessage name="CreateAsIndividual" component="div" className="text-danger" />
                      </div>
                    </div>

                    {/* Tasks of the Day Table - Only show when StartDate === Deadline and IsRepeatable is false */}
                    {!values.IsRepeatable && values.StartDate && values.Deadline && values.StartDate === values.Deadline && (() => {
                      // Fetch tasks when date or assignees change
                      const assigneeIds = getAssigneeIds(values.assigneeList);
                      const currentStartDate = values.StartDate;
                      // Sort to ensure consistent comparison (create a copy to avoid mutating)
                      const assigneeIdsStr = JSON.stringify([...assigneeIds].sort());
                      
                      // Check if values changed and fetch tasks
                      // Using a ref to track previous values and useEffect-like behavior
                      const prevStartDate = prevValuesRef.current.StartDate;
                      const prevAssigneeIdsStr = prevValuesRef.current.assigneeIdsStr;
                      
                      // Check if this is the first render or if values have changed
                      const isFirstRender = prevStartDate === null;
                      const hasChanged = isFirstRender || 
                                        prevStartDate !== currentStartDate || 
                                        prevAssigneeIdsStr !== assigneeIdsStr;
                      
                      if (hasChanged) {
                        // Update ref immediately to prevent duplicate calls
                        prevValuesRef.current.StartDate = currentStartDate;
                        prevValuesRef.current.assigneeIdsStr = assigneeIdsStr;
                        
                        // Use setTimeout to avoid calling during render
                        setTimeout(() => {
                          fetchDayTasks(currentStartDate, assigneeIds);
                        }, 0);
                      }

                      // Define columns for the table
                      const columns = [
                            {
                              key: 'index',
                              header: '#',
                              render: (row, rowIndex) => rowIndex + 1,
                            },
                            {
                              key: 'TaskName',
                              header: 'Task Title',
                              accessor: (row) => row.TaskName || 'N/A',
                            },
                            {
                              key: 'StartTime',
                              header: 'Start Time',
                              accessor: (row) => {
                                if (!row.StartDate) return '';
                                
                                const formattedDate = formatDateToDDMMMYYYY(row.StartDate);
                                
                                // If time exists and is not 00:00, show date and time
                                if (row.StartTime && row.StartTime !== '00:00' && row.StartTime !== '00:00:00') {
                                  const time12 = convert24To12Hour(row.StartTime);
                                  return `${formattedDate}, ${time12.hour}:${String(time12.minute).padStart(2, '0')} ${time12.ampm}`;
                                }
                                
                                // If no time, show only date
                                return formattedDate;
                              },
                            },
                            {
                              key: 'EndTime',
                              header: 'End Time',
                              accessor: (row) => {
                                if (!row.EndDate) return '';
                                
                                const formattedDate = formatDateToDDMMMYYYY(row.EndDate);
                                
                                // If time exists and is not 00:00, show date and time
                                if (row.EndTime && row.EndTime !== '00:00' && row.EndTime !== '00:00:00') {
                                  const time12 = convert24To12Hour(row.EndTime);
                                  return `${formattedDate}, ${time12.hour}:${String(time12.minute).padStart(2, '0')} ${time12.ampm}`;
                                }
                                
                                // If no time, show only date
                                return formattedDate;
                              },
                            },
                            {
                              key: 'Assignees',
                              header: 'Assignees',
                              accessor: (row) => {
                                const assignees = row.Assignees || [];
                                if (assignees.length === 0) return 'Unassigned';
                                return assignees.map(a => a.FullName || a.full_name || 'Unknown').join(', ');
                              },
                            },
                            {
                              key: 'Status',
                              header: 'Status',
                              accessor: (row) => {
                                const status = row.Status !== undefined ? row.Status : (row.status !== undefined ? row.status : 0);
                                const statusLabels = {
                                  0: 'Pending',
                                  1: 'In Progress',
                                  2: 'Postponed',
                                  3: 'Done'
                                };
                                return statusLabels[status] || 'Unknown';
                              },
                            },
                          ];

                          return (
                            <div className="col-lg-12 mb-4" style={{marginTop: '1rem'}}>
                              <label className="text-label mb-3 d-block" style={{fontSize: '1rem'}}>Existing Tasks for the selected day</label>
                              <div style={{ overflow: 'auto' }}>
                                {dayTasksLoading ? (
                                  <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                      <span className="visually-hidden">Loading...</span>
                                    </div>
                                  </div>
                                ) : (
                                  <CommonTable
                                    columns={columns}
                                    data={dayTasks}
                                    emptyMessage="No tasks found for this date"
                                    emptyStateIcon="fas fa-tasks"
                                    getRowId={(row) => row.id}
                                    maxHeight="400px"
                                    minHeight="200px"
                                  />
                                )}
                              </div>
                            </div>
                          );
                      })()}

                    {/* Checklist - always visible, just above settings accordion */}
                    <div className="col-lg-12 mb-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <label className="text-label mb-0">Checklist</label>
                        <i
                          className="fa fa-plus"
                          style={{ color: 'green', cursor: 'pointer', fontSize: '1rem' }}
                          onClick={() => handleChecklistAddGlobal(values, setFieldValue)}
                          title="Add checklist item"
                          aria-label="Add checklist item"
                          tabIndex={0}
                          onKeyPress={e => { if (e.key === 'Enter') handleChecklistAddGlobal(values, setFieldValue); }}
                        />
                      </div>
                      <div className="checklist-container p-3" style={{ background: '#f8f9fa', borderRadius: '8px', padding: '20px 18px', marginBottom: '10px' }}>
                        {values.Checklist.map((field, index) => (
                          <div key={field.id} className="d-flex align-items-center justify-content-between mb-4" style={{ minHeight: '44px' }}>
                            <div className="d-flex align-items-center flex-grow-1 me-3" style={{ gap: '10px' }}>
                              {field.isEditing ? (
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder={`Add the next Checklist item here`}
                                  value={field.value}
                                  onChange={(e) => handleChecklistChange(setFieldValue, index, e.target.value)}
                                  onKeyDown={(e) => handleChecklistKeyDown(e, values, setFieldValue, index)}
                                  onBlur={() => handleChecklistBlur(values, setFieldValue, index)}
                                  ref={(el) => {
                                    if (el) {
                                      checklistInputRefs.current[field.id] = el;
                                    } else {
                                      delete checklistInputRefs.current[field.id];
                                    }
                                  }}
                                />
                              ) : (
                                <div
                                  className={`form-control readonly-box ${field.checked ? 'checked' : ''}`}
                                  onClick={() => handleChecklistEdit(values, setFieldValue, index)}
                                  role="button"
                                  tabIndex={0}
                                  title="Click to edit"
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleChecklistEdit(values, setFieldValue, index); }}
                                >
                                  {field.value}
                                </div>
                              )}
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: '15px' }}>
                              {!field.isEditing && (
                                <i
                                  className="fa fa-edit"
                                  style={{ color: '#0d6efd', cursor: 'pointer', fontSize: '1rem' }}
                                  onClick={() => handleChecklistEdit(values, setFieldValue, index)}
                                  title="Edit item"
                                  aria-label="Edit item"
                                  tabIndex={0}
                                  onKeyPress={e => { if (e.key === 'Enter') handleChecklistEdit(values, setFieldValue, index); }}
                                />
                              )}
                              <i
                                className="fa fa-trash"
                                style={{ color: values.Checklist.length === 1 ? '#ccc' : 'red', cursor: values.Checklist.length === 1 ? 'not-allowed' : 'pointer', fontSize: '1rem' }}
                                onClick={() => values.Checklist.length === 1 ? null : handleChecklistDelete(values, setFieldValue, index)}
                                title="Delete item"
                                aria-label="Delete item"
                                tabIndex={values.Checklist.length === 1 ? -1 : 0}
                                onKeyPress={e => { if (e.key === 'Enter' && values.Checklist.length !== 1) handleChecklistDelete(values, setFieldValue, index); }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Settings Accordion */}
                    <div className="card-header accordion mb-0"
                      style={{ cursor: 'pointer', background: '#ffffff', paddingTop: '1rem', paddingLeft: 0 }}
                      onClick={() => setIsOpen1((prev) => !prev)}
                      aria-expanded={isOpen1}
                    >
                      <h4 className="card-title ">Settings</h4>
                      <i
                        className="fa fa-chevron-down"
                        style={{
                          transition: 'transform 0.3s',
                          transform: isOpen1 ? 'rotate(180deg)' : 'rotate(0deg)',
                          fontSize: '1rem',
                          marginLeft: '8px',
                          color: '#888'
                        }}
                        aria-hidden="true"
                      ></i>
                    </div>

                    {isOpen1 ? (
                      <div className='row mb-2 pt-2' >

                        {/* ProjectId */}
                        <div className="col-lg-6 mb-2">
                          <label className="text-label">Project </label>
                          <Field as="select" className="form-control" name="ProjectId">
                            <option value="">Select Project</option>
                            {projects.map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="ProjectId" component="div" className="text-danger" />
                        </div>

                        {/* Observers */}
                        <div className="col-lg-6 mb-2">
                          <label className="text-label">
                            Observers
                            {!hasObserverAccess && (
                              <i className="fas fa-lock text-warning ms-2" title="This feature is locked in your current plan"></i>
                            )}
                          </label>
                          {hasObserverAccess ? (
                            <Select
                              isMulti
                              styles={customStyles}
                              options={filteredObservers.map((observer) => ({
                                value: observer.id,
                                label: observer.full_name,
                                data: observer
                              }))
                              }
                              value={values.Observers.map((a) => ({
                                value: a.id,
                                label: a.full_name,
                                data: a,
                              }))}
                              onChange={(selectedOptions) =>
                                setFieldValue(
                                  'Observers',
                                  selectedOptions.map((option) => option.data)
                                )
                              }
                            />
                          ) : (
                            <div className="feature-locked-field">
                              <div className="locked-field-content">
                                <i className="fas fa-lock text-muted me-2"></i>
                                <span className="text-muted">Adding observers is not available in your current plan</span>
                              </div>
                            </div>
                          )}
                          <ErrorMessage name="Observers" component="div" className="text-danger" />
                        </div>

                      </div>
                    ) : null}

                    {/* Additional Settings Accordion */}
                    <div className="card-header"
                      style={{ cursor: 'pointer', background: '#ffffff', paddingTop: '1rem', paddingLeft: 0 }}
                      onClick={() => setIsOpen2((prev) => !prev)}
                      aria-expanded={isOpen2}
                    >
                      <h4 className="card-title">Additional Settings</h4>
                      <i
                        className="fa fa-chevron-down"
                        style={{
                          transition: 'transform 0.3s',
                          transform: isOpen2 ? 'rotate(180deg)' : 'rotate(0deg)',
                          fontSize: '1rem',
                          marginLeft: '8px',
                          color: '#888'
                        }}
                        aria-hidden="true"
                      ></i>
                    </div>
                    {isOpen2 ? (
                      <div className='row'>
                        {/* Email Reminder */}
                        <div className="col-lg-3 mb-2">
                          <div className="form-check mt-4">
                            {hasEmailReminderAccess ? (
                              <>
                                <Field
                                  type="checkbox"
                                  className="form-check-input"
                                  name="email_reminder"
                                  checked={hasEmailReminderAccess ? values.email_reminder : false}
                                  onChange={e => setFieldValue('email_reminder', e.target.checked)}
                                />
                                <label className="form-check-label">
                                  Email Reminder
                                  <i 
                                    className="fas fa-info-circle ms-2 text-muted" 
                                    style={{ fontSize: '0.875rem' }}
                                    title="Send email notifications to keep task participants updated on progress and changes"
                                  ></i>
                                </label>
                              </>
                            ) : (
                              <div className="feature-locked-field">
                                <div className="locked-field-content">
                                  <i className="fas fa-lock text-muted me-2"></i>
                                  <span className="text-muted">Email Reminders not available in your plan</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Allow Assignee Update */}
                        <div className="col-lg-3 mb-2">
                          <div className="form-check mt-4">
                            <Field
                              type="checkbox"
                              className="form-check-input"
                              name="is_assignee_update_allowed"
                              checked={values.is_assignee_update_allowed}
                              onChange={e => setFieldValue('is_assignee_update_allowed', e.target.checked)}
                            />
                            <label className="form-check-label">
                              Allow Assignee Update
                              <i 
                                className="fas fa-info-circle ms-2 text-muted" 
                                style={{ fontSize: '0.875rem' }}
                                title="Allow assigned users to update task details, status by themselves"
                              ></i>
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Documents Accordion */}
                    <div className="card-header"
                      style={{ cursor: 'pointer', background: '#ffffff', paddingTop: '1rem', paddingLeft: 0 }}
                      onClick={() => setIsOpen3((prev) => !prev)}
                      aria-expanded={isOpen3}
                    >
                      <h4 className="card-title">
                        Documents
                        {!hasDocumentUploadAccess && (
                          <i className="fas fa-lock text-warning ms-2" title="This feature is locked in your current plan"></i>
                        )}
                      </h4>
                      <i
                        className="fa fa-chevron-down"
                        style={{
                          transition: 'transform 0.3s',
                          transform: isOpen3 ? 'rotate(180deg)' : 'rotate(0deg)',
                          fontSize: '1rem',
                          marginLeft: '8px',
                          color: '#888'
                        }}
                        aria-hidden="true"
                      ></i>
                    </div>
                    {isOpen3 ? (
                      <div className='row pt-2'>
                        {hasDocumentUploadAccess ? (
                          <div className="form-group mb-3 fileupload-group">
                            <label className="text-label mt-2">Attachments</label>
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="form-control fileupload-control"
                              multiple
                              onChange={e => handleFileChange(e, values, setFieldValue)}
                            />
                            <ErrorMessage name="Files" component="div" className="text-danger" />
                          </div>
                        ) : (
                          <div className="col-12 mb-3">
                            <div className="feature-locked-field">
                              <div className="locked-field-content">
                                <i className="fas fa-lock text-muted me-2"></i>
                                <span className="text-muted">Document upload is not available in your current plan</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {hasDocumentUploadAccess && values.Files.length > 0 && (
                          <div className="mt-3">
                            <label className="text-label">Uploaded Files:</label>
                            <ul className="list-unstyled">
                              {values.Files.map((file, index) => {
                                // Determine file extension
                                const extension = (file.file_extension || file.file_name?.split(".").pop() || "").toLowerCase();
                                let fileUrl = "";
                                if (file instanceof File) {
                                  fileUrl = URL.createObjectURL(file);
                                }
                                const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);
                                const isBackend = !(file instanceof File) && file.id;
                                return (
                                  <li key={index} className="mb-2 d-flex align-items-center">
                                    <button
                                      type="button"
                                      onClick={() => handlePreview(file)}
                                      className="btn btn-link me-2"
                                    >
                                      {file.name || file.file_name}
                                    </button>
                                    {!isImage && (fileUrl || isBackend) && (
                                      <a
                                        href={file instanceof File ? fileUrl : '#'}
                                        className="me-4"
                                        title="Download"
                                        style={{ cursor: 'pointer', color: '#2A3335' }}
                                        onClick={async (e) => {
                                          if (file instanceof File) return;
                                          e.preventDefault();
                                          try {
                                            const blob = await fetchTaskAttachmentBlob(file.id);
                                            const u = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = u;
                                            a.download = file.file_name || file.name || 'download';
                                            a.click();
                                            setTimeout(() => URL.revokeObjectURL(u), 60000);
                                          } catch (err) {
                                            toast.error(err.message || 'Download failed');
                                          }
                                        }}
                                      >
                                        <i className="fa fa-download"></i>
                                      </a>
                                    )}
                                    <div
                                      onClick={() => handleRemoveFile(index, values, setFieldValue)}
                                      className="me-2"
                                      style={{ cursor: 'pointer', color: '#2A3335' }}
                                      title="Remove"
                                    >
                                      <i className="fa fa-trash"></i>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}

                        {/* File Preview Modal */}
                        {showPreviewModal && filePreview && (
                          <>
                            <div
                              className="modal-backdrop"
                              style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                                zIndex: 1040,
                              }}
                              onClick={() => setShowPreviewModal(false)}
                            ></div>
                            <div
                              className="modal d-block"
                              style={{
                                position: "fixed",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1050,
                                backgroundColor: "#fff",
                                borderRadius: "10px",
                                padding: "20px",
                                maxWidth: "65%",
                                maxHeight: "90%",
                                overflow: "hidden",
                                boxShadow: "0 4px 15px rgba(0,0,0,0.5)",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">{filePreview.file?.file_name || filePreview.file?.name}</h5>
                                <button
                                  className="btn-close"
                                  onClick={() => setShowPreviewModal(false)}
                                ></button>
                              </div>
                              {(() => {
                                // Robustly determine extension and url
                                let extension = '';
                                let url = '';
                                let fileName = '';
                                let isBlob = filePreview.isBlob;
                                if (filePreview.file) {
                                  extension = (filePreview.file.file_extension || filePreview.file.file_name?.split(".").pop() || '').toLowerCase();
                                  fileName = filePreview.file.file_name || filePreview.file.name || 'file';
                                  if (filePreview.url) {
                                    url = filePreview.url;
                                  }
                                }
                                // If no url, show fallback
                                if (!url) {
                                  return (
                                    <div className="text-center">
                                      <p className="mb-2">No preview available, and no file URL found.</p>
                                    </div>
                                  );
                                }
                                // Image preview
                                if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                      <img
                                        src={url}
                                        alt="Preview"
                                        style={{
                                          maxHeight: "70vh",
                                          objectFit: "contain",
                                          width: "auto",
                                          height: "auto",
                                        }}
                                      />
                                      <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>

                                        <button
                                          type="button"
                                          className="btn btn-secondary ms-2"
                                          onClick={handleOpenInNewTab}
                                        >
                                          Open in new tab
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }
                                // PDF preview
                                if (extension === "pdf") {
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                      <iframe
                                        src={url}
                                        title="PDF Preview"
                                        style={{ width: "100%", height: "70vh", border: "none" }}
                                      />
                                      <div style={{ marginTop: '1rem' }}>

                                        <button
                                          type="button"
                                          className="btn btn-secondary ms-2"
                                          onClick={handleOpenInNewTab}
                                        >
                                          Open in new tab
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }
                                // Office preview
                                if (["doc", "docx", "ppt", "pptx", "xls", "xlsx"].includes(extension)) {
                                  if (isBlob) {
                                    return (
                                      <div className="text-center">
                                        <p className="mb-2">Preview not available for local {extension} files.</p>

                                        <button
                                          type="button"
                                          className="btn btn-secondary ms-2"
                                          onClick={handleOpenInNewTab}
                                        >
                                          Open in new tab
                                        </button>
                                      </div>
                                    );
                                  }
                                  const gviewUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;
                                  return (
                                    <div className="text-center">
                                      <p className="mb-2">Preview not available for local {extension} files.</p>

                                      <button
                                        type="button"
                                        className="btn btn-secondary ms-2"
                                        onClick={handleOpenInNewTab}
                                      >
                                        Open in new tab
                                      </button>
                                    </div>
                                  );
                                }
                                // Fallback for other types
                                return (
                                  <div className="text-center">
                                    <p className="mb-2">No preview available, but you can download or open the file.</p>
                                    <a
                                      href={url}
                                      download={fileName}
                                      className="btn btn-primary"
                                    >
                                      Download
                                    </a>
                                    <button
                                      type="button"
                                      className="btn btn-secondary ms-2"
                                      onClick={handleOpenInNewTab}
                                    >
                                      Open in new tab
                                    </button>
                                  </div>
                                );
                              })()}
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}

                    {/* Action Buttons - wrapped in row/col for consistent alignment with form fields */}
                    <div className="row mt-3">
                      <div className="col-12">
                        <div className="task-form-toolbar text-end toolbar toolbar-bottom">
                      {onCancel && (
                        <button
                          type="button"
                          className="btn btn-cancel task-form-toolbar-btn"
                          onClick={onCancel}
                          disabled={isSubmitting || isRedirecting}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        className="btn btn-primary sw-btn-next task-form-toolbar-btn"
                        type="submit"
                        disabled={isSubmitting || isRedirecting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2 " role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : isRedirecting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2 " role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          mode === 'edit' ? 'Update' : 'Save'
                        )}
                      </button>
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskForm; 